import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Github, Linkedin, Mail, ArrowRight, Send } from "lucide-react";
import { personalInfo } from "../data/portfolio";

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Replace with your form submission logic (e.g., EmailJS, Formspree)
    setSent(true);
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <section id="contact" className="relative px-6 md:px-16 lg:px-24 py-32">
      <div className="max-w-6xl mx-auto">
        <div ref={ref}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-label mb-3"
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400">
              07 // Contact
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
            className="font-display font-black text-4xl md:text-6xl tracking-tight mb-4"
          >
            Let's
            <br />
            <span className="grad-text">connect.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/40 text-base max-w-lg mb-14"
          >
            Open to full-time roles, freelance projects, research
            collaborations, and interesting conversations. Let's build something
            impactful together.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.23, 1, 0.32, 1] }}
            className="glass-card p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block font-mono text-[11px] tracking-widest uppercase text-white/30 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={formState.name}
                  onChange={(e) =>
                    setFormState({ ...formState, name: e.target.value })
                  }
                  placeholder="Your name"
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-blue-400/40 focus:bg-white/[0.05] transition-all duration-200 font-mono"
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] tracking-widest uppercase text-white/30 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formState.email}
                  onChange={(e) =>
                    setFormState({ ...formState, email: e.target.value })
                  }
                  placeholder="your@email.com"
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-blue-400/40 focus:bg-white/[0.05] transition-all duration-200 font-mono"
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] tracking-widest uppercase text-white/30 mb-2">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={formState.message}
                  onChange={(e) =>
                    setFormState({ ...formState, message: e.target.value })
                  }
                  placeholder="Tell me about your project or opportunity..."
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-blue-400/40 focus:bg-white/[0.05] transition-all duration-200 font-mono resize-none"
                />
              </div>
              <button
                type="submit"
                data-hover
                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-lg font-semibold text-sm text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(96,165,250,0.3)] ${
                  sent ? "opacity-80" : ""
                }`}
                style={{
                  background: "linear-gradient(135deg, #60a5fa, #a78bfa)",
                }}
              >
                {sent ? (
                  <>Message sent! ✓</>
                ) : (
                  <>
                    <Send size={14} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Right side: direct links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{
              duration: 0.7,
              delay: 0.45,
              ease: [0.23, 1, 0.32, 1],
            }}
            className="space-y-4"
          >
            <p className="font-mono text-xs tracking-widest uppercase text-white/25 mb-6">
              Or reach me directly
            </p>

            {[
              {
                Icon: Mail,
                label: "Email",
                value: personalInfo.email,
                href: `mailto:${personalInfo.email}`,
                color: "#60a5fa",
              },
              {
                Icon: Github,
                label: "GitHub",
                value: "github.com/Ujjwal092",
                href: personalInfo.github,
                color: "#a78bfa",
              },
              {
                Icon: Linkedin,
                label: "LinkedIn",
                value: "linkedin.com/in/ujjwalkumar0920",
                href: personalInfo.linkedin,
                color: "#34d399",
              },
            ].map(({ Icon, label, value, href, color }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-hover
                className="glass-card flex items-center gap-4 p-5 group"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: `${color}15`,
                    border: `1px solid ${color}30`,
                  }}
                >
                  <Icon size={16} style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/25 mb-0.5">
                    {label}
                  </p>
                  <p className="text-sm font-medium text-white/60 group-hover:text-white transition-colors truncate">
                    {value}
                  </p>
                </div>
                <ArrowRight
                  size={14}
                  className="text-white/20 group-hover:text-white/60 group-hover:translate-x-1 transition-all duration-200 shrink-0"
                />
              </a>
            ))}

            {/* Status */}
            <div className="glass-card p-5 mt-2">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_8px_#34d399] animate-pulse" />
                <span className="font-mono text-xs text-white/40">
                  Currently available
                </span>
              </div>
              <p className="text-xs text-white/25 font-mono leading-relaxed">
                Typically respond within 24 hours. Based in — open to remote
                worldwide.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
